import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Activity as ActivityIcon, ArrowDownToLine, ArrowUpFromLine, ChevronLeft, ChevronRight,
  CircleUser, Crown, FolderPen, FolderPlus, Flame, KeyRound, Link2, LogIn, LogOut,
  PackageOpen, Pencil, ShieldAlert, Sparkles, Trash2, Wallet,
} from 'lucide-react';
import { CenteredSpinner, Chip, EmptyState, ErrorState } from '@/components/ui';
import { cn, formatDate } from '@/lib/format';
import { useActivity, useActivityStats } from '@/lib/queries';
import { t } from '@/lib/i18n';
import type { ActivityRow } from '@/lib/api';

/**
 * Admin-only activity log — the viewer half of the Kbiz/UserlogBundle parity
 * work, over `activity_log` (see docs/ACTIVITY-LOG.md).
 *
 * Two panes rather than the bundle's four separate screens (`assetlog`,
 * `userlog`, `aiusagelog`): a rollup strip answering "what is being used and by
 * whom", and one filterable feed underneath. The old screens differed only in
 * which events they showed, which is a filter, not a page.
 */

const PAGE_SIZE = 50;

/** Windows offered for both the rollups and the feed. */
const ranges = () => [
  { days: 7, label: t('activityPage.7days') },
  { days: 30, label: t('activityPage.30days') },
  { days: 90, label: t('activityPage.90days') },
  { days: 3650, label: t('activityPage.allTime') },
];

/**
 * Icon + label per event kind, mirroring the emoji the Telegram alerts use so
 * the same event reads the same way in both places.
 */
const kinds = (): Record<string, { label: string; icon: typeof LogIn; tone: string }> => ({
  'login.success': { label: t('activityPage.login'), icon: LogIn, tone: 'text-emerald-600 dark:text-emerald-400' },
  'login.failed': { label: t('activityPage.loginFailed'), icon: ShieldAlert, tone: 'text-red-600 dark:text-red-400' },
  logout: { label: t('activityPage.logout'), icon: LogOut, tone: 'text-muted' },
  'asset.download': { label: t('activityPage.download'), icon: ArrowDownToLine, tone: 'text-brand-600 dark:text-brand-400' },
  'asset.upload': { label: t('activityPage.upload'), icon: ArrowUpFromLine, tone: 'text-emerald-600 dark:text-emerald-400' },
  'asset.delete': { label: t('activityPage.assetDeleted'), icon: Trash2, tone: 'text-red-600 dark:text-red-400' },
  'asset.update': { label: t('activityPage.assetUpdated'), icon: Pencil, tone: 'text-muted' },
  'asset.move': { label: t('activityPage.assetMoved'), icon: PackageOpen, tone: 'text-muted' },
  'folder.create': { label: t('activityPage.folderCreated'), icon: FolderPlus, tone: 'text-muted' },
  'folder.rename': { label: t('activityPage.folderRenamed'), icon: FolderPen, tone: 'text-muted' },
  'folder.delete': { label: t('activityPage.folderDeleted'), icon: Flame, tone: 'text-red-600 dark:text-red-400' },
  'user.admin': { label: t('activityPage.adminRights'), icon: Crown, tone: 'text-amber-600 dark:text-amber-400' },
  'user.active': { label: t('activityPage.accountEnabledDisabled'), icon: KeyRound, tone: 'text-amber-600 dark:text-amber-400' },
  'tagging.prompt': { label: t('activityPage.aiPromptChanged'), icon: Sparkles, tone: 'text-amber-600 dark:text-amber-400' },
  'tagging.bulk': { label: t('activityPage.bulkTagging'), icon: Wallet, tone: 'text-amber-600 dark:text-amber-400' },
  'share.create': { label: t('activityPage.shareLink'), icon: Link2, tone: 'text-brand-600 dark:text-brand-400' },
});

function kindMeta(kind: string) {
  return kinds()[kind] ?? { label: kind, icon: ActivityIcon, tone: 'text-muted' };
}

export function ActivityPage() {
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState<string | undefined>();
  const [actor, setActor] = useState<string | undefined>();
  const [page, setPage] = useState(0);

  // Any filter change invalidates the current offset — page 3 of "all events"
  // is not page 3 of "downloads by jane".
  const reset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(0); };

  const filter = { days, kind, actor, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
  const { data, isPending, error, refetch } = useActivity(filter);
  const { data: stats } = useActivityStats(days);

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="w-full px-6 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <ActivityIcon className="size-5 text-brand-500" />
          <h1 className="text-lg font-semibold text-strong">{t('activityPage.activity')}</h1>
          {total > 0 && <span className="text-sm text-muted">· {t('activityPage.nEvents', { n: total.toLocaleString() })}</span>}
          <div className="ml-auto flex gap-1">
            {ranges().map((r) => (
              <button
                key={r.days}
                onClick={() => reset(setDays)(r.days)}
                className={cn(
                  'rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
                  days === r.days ? 'bg-brand-500/15 text-brand-600' : 'text-muted hover:text-strong hover:surface-hover',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* ---- rollups ---- */}
        {stats && (
          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <RollupCard
              title={t('activityPage.mostDownloaded')}
              icon={<ArrowDownToLine className="size-4" />}
              empty={t('activityPage.noDownloads')}
              rows={stats.topAssets.map((a) => ({
                key: `${a.assetId}-${a.subject}`,
                // Link through to the asset when we know which one it was; rows
                // predating the assetId column, or from a deleted asset, still
                // show their filename as plain text.
                label: a.assetId ? (
                  <Link to="/assets/$assetId" params={{ assetId: a.assetId }} className="hover:text-brand-600 hover:underline">
                    {a.subject ?? t('activityPage.assetIdLabel', { id: a.assetId })}
                  </Link>
                ) : (a.subject ?? '—'),
                n: a.n,
              }))}
            />
            <RollupCard
              title={t('activityPage.mostActive')}
              icon={<CircleUser className="size-4" />}
              empty={t('activityPage.noActivityPeriod')}
              rows={stats.topActors.map((a) => ({
                key: a.actor ?? 'anon',
                label: (
                  <button
                    onClick={() => reset(setActor)(a.actor ?? undefined)}
                    className="hover:text-brand-600 hover:underline"
                  >
                    {a.actor ?? t('activityPage.anonymous')}
                  </button>
                ),
                n: a.n,
              }))}
            />
            <RollupCard
              title={t('activityPage.failedLoginsByIp')}
              icon={<ShieldAlert className="size-4" />}
              empty={t('activityPage.noFailedLogins')}
              tone={stats.failedLogins.length ? 'warn' : undefined}
              rows={stats.failedLogins.map((f) => ({ key: f.ip ?? '?', label: f.ip ?? t('activityPage.unknown'), n: f.n }))}
            />
          </div>
        )}

        {/* ---- kind filter ---- */}
        {stats && stats.byKind.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <FilterPill active={!kind} onClick={() => reset(setKind)(undefined)}>
              {t('activityPage.all')}
            </FilterPill>
            {stats.byKind.map((k) => (
              <FilterPill key={k.kind} active={kind === k.kind} onClick={() => reset(setKind)(k.kind)}>
                {kindMeta(k.kind).label}
                <span className="ml-1 text-faint">{k.n}</span>
              </FilterPill>
            ))}
          </div>
        )}

        {actor && (
          <div className="mb-3 flex items-center gap-2 text-sm">
            <span className="text-muted">{t('activityPage.filteredTo')}</span>
            <Chip tone="brand">{actor}</Chip>
            <button onClick={() => reset(setActor)(undefined)} className="text-xs text-muted hover:text-strong hover:underline">
              {t('activityPage.clear')}
            </button>
          </div>
        )}

        {/* ---- feed ---- */}
        {isPending ? (
          <CenteredSpinner label={t('activityPage.loading')} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data || data.items.length === 0 ? (
          <div className="rounded-xl border line surface-card">
            <EmptyState
              icon={<ActivityIcon />}
              title={t('activityPage.noActivityRecorded')}
              description={
                kind || actor
                  ? t('activityPage.nothingMatches')
                  : t('activityPage.eventsAppear')
              }
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl border line surface-card">
              <table className="w-full text-sm">
                <thead className="surface-sunken text-left text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t('activityPage.event')}</th>
                    <th className="px-4 py-2 font-medium">{t('activityPage.who')}</th>
                    <th className="px-4 py-2 font-medium">{t('activityPage.subject')}</th>
                    <th className="px-4 py-2 font-medium">{t('activityPage.when')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <Row key={row.id} row={row} onActor={(a) => reset(setActor)(a)} />
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-muted">
                  {t('activityPage.rangeOf', {
                    from: (page * PAGE_SIZE + 1).toLocaleString(),
                    to: Math.min((page + 1) * PAGE_SIZE, total).toLocaleString(),
                    total: total.toLocaleString(),
                  })}
                </span>
                <div className="flex items-center gap-1">
                  <PageButton disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeft className="size-4" />
                  </PageButton>
                  <span className="px-2 text-xs text-muted">
                    {page + 1} / {pages}
                  </span>
                  <PageButton disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight className="size-4" />
                  </PageButton>
                </div>
              </div>
            )}
          </>
        )}

        <p className="mt-4 text-xs text-muted">
          {t('activityPage.footerNoteA')}{' '}
          <code className="text-faint">ALERT_MUTED_KINDS</code>{' '}
          {t('activityPage.footerNoteB')}
        </p>
      </div>
    </div>
  );
}

function Row({ row, onActor }: { row: ActivityRow; onActor: (actor: string | undefined) => void }) {
  const meta = kindMeta(row.kind);
  const Icon = meta.icon;
  const details = Object.entries(row.detail ?? {});

  return (
    <tr className="border-t line align-top">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Icon className={cn('size-4 shrink-0', meta.tone)} aria-hidden />
          <span className="font-medium text-strong">{meta.label}</span>
        </div>
        {details.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1 pl-6">
            {details.map(([k, v]) => (
              <span key={k} className="rounded surface-sunken px-1.5 py-0.5 text-[11px] text-muted">
                {k}: {String(v)}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        <button onClick={() => onActor(row.actor ?? undefined)} className="text-body hover:text-brand-600 hover:underline">
          {row.actor ?? t('activityPage.anonymous')}
        </button>
        {row.ip && <div className="text-xs text-faint">{row.ip}</div>}
      </td>
      <td className="max-w-xs px-4 py-2.5">
        {row.subject ? (
          row.assetId ? (
            <Link to="/assets/$assetId" params={{ assetId: row.assetId }} className="block truncate text-body hover:text-brand-600 hover:underline" title={row.subject}>
              {row.subject}
            </Link>
          ) : (
            <span className="block truncate text-body" title={row.subject}>{row.subject}</span>
          )
        ) : (
          <span className="text-faint">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-muted">{formatDate(row.createdAt)}</td>
    </tr>
  );
}

function RollupCard({ title, icon, rows, empty, tone }: {
  title: string;
  icon: React.ReactNode;
  rows: { key: string; label: React.ReactNode; n: number }[];
  empty: string;
  tone?: 'warn';
}) {
  const top = rows.slice(0, 5);
  const max = Math.max(1, ...top.map((r) => r.n));
  return (
    <div className="rounded-xl border line surface-card p-4">
      <div className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        <span className={cn(tone === 'warn' && rows.length ? 'text-amber-600 dark:text-amber-400' : '')}>{icon}</span>
        {title}
      </div>
      {top.length === 0 ? (
        <p className="text-sm text-faint">{empty}</p>
      ) : (
        <ul className="space-y-1.5">
          {top.map((r) => (
            <li key={r.key} className="text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate text-body">{r.label}</span>
                <span className="shrink-0 tabular-nums text-muted">{r.n.toLocaleString()}</span>
              </div>
              {/* Proportional bar — the ranking is the point, so scale to the
                  leader rather than to the total. */}
              <div className="mt-1 h-1 overflow-hidden rounded-full surface-sunken">
                <div
                  className={cn('h-full rounded-full', tone === 'warn' ? 'bg-amber-500' : 'bg-brand-500')}
                  style={{ width: `${(r.n / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        active ? 'bg-brand-500/15 text-brand-600' : 'surface-sunken text-muted hover:text-strong',
      )}
    >
      {children}
    </button>
  );
}

function PageButton({ disabled, onClick, children }: { disabled: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-grid size-7 place-items-center rounded-lg surface-sunken text-muted transition-colors hover:text-strong disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
